import { Storage } from '../lib/storage';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, QrCode, LogIn, Loader2, DownloadCloud, Store, UserCheck, FileText, Camera, CheckCircle, X, User, IdCard, Phone, ShieldCheck, ArrowRight, Lock, Building2, Fingerprint } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { supabase } from '../lib/supabase';
import bcrypt from 'bcryptjs';
// import { NativeBiometric } from '@capacitor-community/native-biometric';

export type Role = 'motero' | 'motero_dueño' | 'empleado_taller' | null;

export default function LoginScreen({ onLogin }: { onLogin: (role: Role, isNew?: boolean) => void }) {
  const [loading, setLoading] = useState(false);
  const [celular, setCelular] = useState(() => Storage.get('gravity_ultimo_celular') || '');
  const [pin, setPin] = useState('');
  const [askingPin, setAskingPin] = useState(false);
  const [creatingPinForOldUser, setCreatingPinForOldUser] = useState(false);
  const [firstPin, setFirstPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [customAlert, setCustomAlert] = useState<{title: string, message: string} | null>(null);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [scanQRMode, setScanQRMode] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [justInstalled, setJustInstalled] = useState(false);
  const [closeAttempted, setCloseAttempted] = useState(false);

  // New Commercial Flow States
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [subiendoTaller, setSubiendoTaller] = useState(false);
  const [loginMode, setLocalLoginMode] = useState<string | null>(Storage.get('gravity_login_mode'));

  // Master Registration States (New continental standard)
  const [regStep, setRegStep] = useState(1);
  const [commercialFlow, setCommercialFlow] = useState<'login' | 'options' | 'register'>('login');
  const [nombrePropietario, setNombrePropietario] = useState('');
  const [cedulaPropietario, setCedulaPropietario] = useState('');
  const [nombreTaller, setNombreTaller] = useState('');
  const [nitTaller, setNitTaller] = useState('');
  const [celularTaller, setCelularTaller] = useState('');
  const [archivos, setArchivos] = useState<{[key: string]: File | null}>({
    cedula: null,
    camara: null,
    rut: null,
    recibo: null
  });

  useEffect(() => {
    const checkBiometric = async () => {
      try {
        /*
        const result = await NativeBiometric.isAvailable();
        if (result.isAvailable) setBiometricAvailable(true);
        */
      } catch (e) {}
    };
    checkBiometric();

    // Detección Web PWA Estándar (La app es 100% versión web y no se publicará en Play Store)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;

    // Solo se considera instalada si ya está abierta en modo de ventana autónoma (PWA instalada)
    if (isStandalone || isIOSStandalone) {
      setIsInstalled(true);
    }

    // Si ya lo atrapó el script en el HTML:
    if ((window as any).deferredPrompt) {
      setDeferredPrompt((window as any).deferredPrompt);
    }

    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      (window as any).deferredPrompt = e;
    };

    const readyHandler = () => {
      setDeferredPrompt((window as any).deferredPrompt);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('pwaReady', readyHandler);

    const appInstalledHandler = () => {
      setIsInstalled(true);
      setJustInstalled(true);
      setTimeout(() => {
        window.close();
      }, 1500);
    };
    window.addEventListener('appinstalled', appInstalledHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('pwaReady', readyHandler);
      window.removeEventListener('appinstalled', appInstalledHandler);
    };
  }, []);

  const handleCloseNavegador = () => {
    setCloseAttempted(true);
    // Intento programático
    window.close();
    
    // Hack clásico de self-close para engañar al sandbox
    try {
      window.open('', '_self', '');
      window.close();
    } catch(e) {}

    // Vaciar pestaña si no se cerró tras un segundo
    setTimeout(() => {
      try {
        window.location.href = 'about:blank';
      } catch(e) {}
    }, 1200);
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      alert("Para instalar Gravity Bike en este dispositivo, selecciona 'Agregar a la pantalla principal' o 'Instalar aplicación' desde el menú de opciones de tu navegador web.");
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  useEffect(() => {
    let html5QrCode: Html5Qrcode;

    if (scanQRMode) {
      setTimeout(() => {
          html5QrCode = new Html5Qrcode('reader');
          html5QrCode.start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 250, height: 250 } },
            (decodedText) => {
              if (html5QrCode.isScanning) {
                html5QrCode.stop().then(() => {
                  html5QrCode.clear();
                  setScanQRMode(false);
                  
                  if (decodedText.startsWith('TALLER_ACCESO:')) {
                    const ownerId = decodedText.split(':')[1];
                    
                    // Forzar el almacenamiento para App.tsx y TallerScreen
                    Storage.set('gravity_user_id', ownerId);
                    Storage.set('gravity_user', JSON.stringify({
                       rol: 'empleado_taller',
                       nombre: 'Mecánico de Turno',
                       celular: 'QR_ACCESS'
                    }));

                    alert("QR Verificado. Acceso Empleado Concedido.");
                    onLogin('empleado_taller', false);
                  } else {
                    alert("QR Inválido o Expirado");
                  }
                }).catch(console.error);
              }
            },
            () => {} // Ignorar advertencias de lectura en progreso
          ).catch((err) => {
            console.error("No se pudo iniciar cámara trasera.", err);
            // Intentar sin forzar la cámara trasera si falla
            html5QrCode.start(
               { facingMode: "user" },
               { fps: 10, qrbox: { width: 250, height: 250 } },
               (t) => {
                   // Fallback
                   setScanQRMode(false); 
                   alert("QR Fallback leído: " + t); 
               },
               () => {}
            ).catch(e => console.error("Fallo total de cámara", e));
          });
      }, 100); // Pequeño retraso para que el div 'reader' renderice

      return () => {
        if (html5QrCode && html5QrCode.isScanning) {
          html5QrCode.stop().then(() => html5QrCode.clear()).catch(console.error);
        }
      };
    }
  }, [scanQRMode, onLogin]);

  const [tempUser, setTempUser] = useState<any>(null);

  const restoreSOSFromDB = (userData: any) => {
    if (userData && userData.contactos_sos) {
      const sos = userData.contactos_sos;
      if (sos.fam1Nombre) Storage.set('gravity_sos_fam1Nombre', sos.fam1Nombre);
      if (sos.fam1Celular) Storage.set('gravity_sos_fam1Celular', sos.fam1Celular);
      if (sos.motero1Nombre) Storage.set('gravity_sos_motero1Nombre', sos.motero1Nombre);
      if (sos.motero1Celular) Storage.set('gravity_sos_motero1Celular', sos.motero1Celular);
      if (sos.motero2Nombre) Storage.set('gravity_sos_motero2Nombre', sos.motero2Nombre);
      if (sos.motero2Celular) Storage.set('gravity_sos_motero2Celular', sos.motero2Celular);
      if (sos.motero3Nombre) Storage.set('gravity_sos_motero3Nombre', sos.motero3Nombre);
      if (sos.motero3Celular) Storage.set('gravity_sos_motero3Celular', sos.motero3Celular);
    }
  };

  const handleVerifyPin = async (completedPin: string) => {
    if (!tempUser) return;
    
    setLoading(true);
    try {
      // 1. Consultar el usuario de la DB por ID
      const { data, error } = await supabase
        .from('usuarios')
        .select('*')
        .eq('id', tempUser.id)
        .single();

      if (error || !data) {
        setPin('');
        setPinError('Error de conexión o usuario no encontrado.');
        setTimeout(() => setPinError(''), 4000);
        return;
      }

      let pinCorrecto = false;

      // 2. Dual-mode check: Texto plano (legacy) o Hash encriptado (pgcrypto / bcrypt)
      if (data.pin === completedPin) {
        pinCorrecto = true;
      } else if (data.pin && (data.pin.startsWith('$') || data.pin.length > 20)) {
        // Primero intentamos verificación local ultra-rápida y tolerante a fallos de base de datos
        try {
          if (bcrypt.compareSync(completedPin, data.pin)) {
            pinCorrecto = true;
          }
        } catch (bcryptErr) {
          console.error("Local bcrypt check failed, falling back to RPC:", bcryptErr);
        }

        // Si falló localmente, llamamos al RPC de Supabase como fallback seguro
        if (!pinCorrecto) {
          const { data: rpcMatch, error: rpcErr } = await supabase.rpc('verificar_pin_usuario', {
            p_usuario_id: tempUser.id,
            p_pin_ingresado: completedPin
          });
          if (!rpcErr && rpcMatch === true) {
            pinCorrecto = true;
          }
        }
      }

      if (pinCorrecto) {
        const formatName = (str: string) => {
          if (!str) return '';
          return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        };
        const activeName = formatName(data.nombre || data.alias || '');
        const activeRol = data.rol || 'motero';

        Storage.set('gravity_user', JSON.stringify({id: data.id, celular: data.celular, rol: activeRol, nombre: activeName, es_oro: data.es_oro, pin: data.pin, email: data.email}));
        Storage.set('gravity_user_id', data.id);
        Storage.set('gravity_ultimo_celular', data.celular);
        
        restoreSOSFromDB(data);
        
        onLogin(activeRol, false);
      } else {
        setPin('');
        setPinError('PIN incorrecto. Intenta de nuevo.');
        setTimeout(() => setPinError(''), 4000);
      }
    } catch (e) {
      setPin('');
      setPinError('Error de servidor en inicio de sesión.');
      setTimeout(() => setPinError(''), 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePinForOldUser = async (newPin: string) => {
    if (!firstPin) {
      setFirstPin(newPin);
      setPin('');
      return;
    }
    
    if (firstPin !== newPin) {
      setPinError('Los PINs no coinciden. Intenta de nuevo.');
      setPin('');
      setFirstPin('');
      setTimeout(() => setPinError(''), 4000);
      return;
    }

    if (!tempUser) return;
    setLoading(true);
    try {
      await supabase.from('usuarios').update({ pin: newPin }).eq('id', tempUser.id);
      
      const formatName = (str: string) => {
        if (!str) return '';
        return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      };
      const activeName = formatName(tempUser.nombre || tempUser.alias || '');
      const activeRol = tempUser.rol || 'motero';

      Storage.set('gravity_user', JSON.stringify({id: tempUser.id, celular: tempUser.celular, rol: activeRol, nombre: activeName, es_oro: tempUser.es_oro, pin: newPin, email: tempUser.email}));
      Storage.set('gravity_user_id', tempUser.id);
      Storage.set('gravity_ultimo_celular', tempUser.celular);
      
      restoreSOSFromDB(tempUser);
      
      onLogin(activeRol, false);
    } catch (e) {
      alert("Error guardando tu nuevo PIN.");
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async () => {
    if (celular.length < 10) {
      setCustomAlert({ title: 'Número Inválido', message: 'Por favor ingresa un celular de al menos 10 dígitos.' });
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('*')
        .eq('celular', celular)
        .single();
        
      if (data) {
        if (loginMode === 'business') {
          const { data: tallerData } = await supabase
            .from('talleres_aliados')
            .select('id')
            .eq('usuario_id', data.id)
            .limit(1);
            
          if (!tallerData || tallerData.length === 0) {
             setCustomAlert({ title: 'Acceso Denegado', message: 'No se encontró un taller activo o aprobado asociado a este número. Si enviaste una solicitud, está en revisión.' });
             setLoading(false);
             return;
          }
        }
        
        // PERFIL EXISTENTE
        setTempUser(data);
        if (data.pin) {
           setAskingPin(true);
        } else {
           // Veterano sin PIN: lo creamos aquí
           setCreatingPinForOldUser(true);
        }
      } else {
        // PERFIL NUEVO: Empieza el proceso de Onboarding
        if (loginMode === 'business') {
           setCustomAlert({ title: 'Acceso Denegado', message: 'No estás registrado. Por favor ingresa por el Modo Motero para crear tu cuenta.' });
           setLoading(false);
           return;
        }
        Storage.remove('gravity_nombre_perfil');
        Storage.remove('gravity_alias');
        Storage.remove('gravity_user_id');
        Storage.remove('gravity_user');
        
        Storage.set('gravity_ultimo_celular', celular);
        Storage.set('gravity_temp_phone', celular);
        onLogin('motero', true);
      }
    } catch (err) {
      // No existe -> Nuevo
      if (loginMode === 'business') {
         setCustomAlert({ title: 'Acceso Denegado', message: 'No estás registrado. Por favor ingresa por el Modo Motero para crear tu cuenta.' });
         setLoading(false);
         return;
      }
      Storage.set('gravity_temp_phone', celular);
      onLogin('motero', true);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0];
      if (f.size > 512 * 1024) {
        alert("⚠️ El archivo "+f.name+" pesa "+(f.size/1024).toFixed(0)+"KB, excediendo el límite de 512KB. Comprímelo o toma una foto de menor resolución.");
        e.target.value = '';
        return;
      }
      setArchivos(prev => ({...prev, [key]: f}));
    }
  };

  const handleRegisterTaller = async () => {
    // Validación de Fase 2 (Negocio)
    if (!nombreTaller || !nitTaller || !archivos.cedula || !archivos.camara || !archivos.rut || !archivos.recibo) {
      alert("Por favor completa los datos del negocio y sube los 4 documentos.");
      return;
    }

    setSubiendoTaller(true);
    try {
      // 1. Inyectar Identidad en Usuarios (Nacimiento Digital)
      const { data: existingUser } = await supabase.from('usuarios').select('id').eq('celular', celularTaller).maybeSingle();
      
      let masterUserId = existingUser?.id;

      if (!masterUserId) {
        // Crear usuario nuevo si no existe
        const { data: newUser, error: createError } = await supabase.from('usuarios').insert([{
           celular: celularTaller,
           nombre: nombrePropietario,
           identificacion: cedulaPropietario,
           rol: 'motero', // Inicia como motero hasta ser aprobado
           alias: nombrePropietario.split(' ')[0]
        }]).select('id').single();
        
        if (createError) throw createError;
        masterUserId = newUser.id;
      } else {
        // Actualizar datos si ya existe (Mantener coherencia)
        await supabase.from('usuarios').update({
           nombre: nombrePropietario,
           identificacion: cedulaPropietario
        }).eq('id', masterUserId);
      }

      const getExtension = (f: File) => f.name.toLowerCase().endsWith('pdf') ? 'pdf' : 'jpg';
      const toBase64 = (file: File) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = error => reject(error);
      });

      const payload = {
        nombre: nombreTaller,
        nit: nitTaller,
        celular: celularTaller,
        propietario: nombrePropietario,
        cedula_propietario: cedulaPropietario,
        archivos: [
          { name: "Cedula_"+nitTaller+"."+getExtension(archivos.cedula as File), type: archivos.cedula?.type, data: await toBase64(archivos.cedula as File) },
          { name: "Camara_"+nitTaller+"."+getExtension(archivos.camara as File), type: archivos.camara?.type, data: await toBase64(archivos.camara as File) },
          { name: "RUT_"+nitTaller+"."+getExtension(archivos.rut as File), type: archivos.rut?.type, data: await toBase64(archivos.rut as File) },
          { name: "Recibo_"+nitTaller+"."+getExtension(archivos.recibo as File), type: archivos.recibo?.type, data: await toBase64(archivos.recibo as File) }
        ]
      };

      // 2. Enviar a Google Apps Script (Auditoría con metadato de celular)
      await fetch("https://script.google.com/macros/s/AKfycbyUebMoi25UCpX7_gIs-CpnwmdAbYCLPh4MD1DZEM380J6Gq3LI6UAw68_MSXF-8boT_w/exec", {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload)
      });

      // 3. Guardar trazabilidad vinculada en Supabase (Solo campos existentes)
      const { error: solError } = await supabase.from('solicitudes_taller').insert([{
         usuario_id: masterUserId,
         nombre_taller: nombreTaller,
         nit: nitTaller,
         estado: 'pendiente'
      }]);

      if (solError) throw solError;

      setRegisterSuccess(true);
    } catch (e: any) {
      console.error(e);
      alert("Error Maestro: " + (e.message || JSON.stringify(e)) + "\n\nVerifica tu conexión o contacta a soporte.");
    } finally {
      setSubiendoTaller(false);
    }
  };

  const PinKeypad = ({ onComplete }: { onComplete: (val: string) => void }) => {
    const handleDigit = (d: string) => {
      if (pin.length < 4) {
        const newPin = pin + d;
        setPin(newPin);
        if (newPin.length === 4) onComplete(newPin);
      }
    };

    const handleDelete = () => setPin(pin.slice(0, -1));

    return (
      <div className="flex flex-col items-center gap-4 mt-4">
        <div className="flex gap-3 mb-2">
          {[0,1,2,3].map(i => (
            <motion.div 
              key={i}
              animate={{ scale: pin.length > i ? 1.2 : 1, backgroundColor: pin.length > i ? '#00a8ff' : 'rgba(255,255,255,0.1)' }}
              className="w-4 h-4 rounded-full border border-white/20 shadow-cyan-glow"
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3,4,5,6,7,8,9].map(d => (
            <button 
              key={d} 
              onClick={() => handleDigit(d.toString())}
              className="w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white font-black text-xl hover:bg-primary-cyan/20 transition-all active:scale-90"
            >
              {d}
            </button>
          ))}
          <button onClick={() => setPin('')} className="w-14 h-14 rounded-full bg-danger-red/10 text-danger-red font-bold text-[9px] uppercase">Borrar</button>
          <button onClick={() => handleDigit('0')} className="w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white font-black text-xl hover:bg-primary-cyan/20 transition-all active:scale-90">0</button>
          <button onClick={handleDelete} className="w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10 active:scale-90">
             <ArrowRight className="w-6 h-6 rotate-180" />
          </button>
        </div>
      </div>
    );
  };

  const handleBiometricAuth = async () => {
    /*
    try {
      const verified = await NativeBiometric.verify({
        reason: "Autenticación para Bóveda MotoPikis",
        title: "MOTOPIKIS Pro",
        subtitle: "Acceso Seguro",
        description: "Usa tu huella o rostro para entrar.",
      });
      
      const savedUser = Storage.get('gravity_user');
      if (savedUser) {
        const u = JSON.parse(savedUser);
        onLogin(u.rol, false);
      }
    } catch (e) {
      alert("Error Biométrico o Cancelado.");
    }
    */
  };

  if (justInstalled) {
    return (
      <div className="mobile-simulator bg-bg-dark text-text-main h-full flex flex-col items-center justify-center p-8 text-center relative overflow-hidden bg-black">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary-cyan/10 rounded-full blur-[100px] pointer-events-none" />
        
        <div className="w-20 h-20 bg-primary-cyan/10 rounded-full flex items-center justify-center border border-primary-cyan/20 mb-8 animate-bounce shadow-cyan-glow">
          <CheckCircle className="w-10 h-10 text-primary-cyan" />
        </div>
        
        <h2 className="text-white font-black text-2xl tracking-wide uppercase mb-4">¡Instalación Exitosa!</h2>
        
        <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-xs mx-auto">
          <strong>Gravity Bike App</strong> se ha instalado y abierto de forma independiente en tu dispositivo.
        </p>
        
        <p className="text-slate-500 text-xs leading-relaxed mb-10 max-w-xs mx-auto">
          Ya puedes cerrar de forma segura esta pestaña del navegador e iniciar la app directamente desde tu pantalla de inicio.
        </p>
        
        <button
          onClick={handleCloseNavegador}
          className="w-full max-w-xs py-4 bg-primary-cyan rounded-2xl font-black text-xs uppercase tracking-widest text-white shadow-cyan-glow hover:bg-primary-hover active:scale-95 transition-all"
        >
          {closeAttempted ? 'Pestaña Lista Para Cerrar' : 'Cerrar Navegador'}
        </button>

        {closeAttempted && (
          <p className="text-amber-400 text-[11px] font-black uppercase mt-5 animate-pulse max-w-xs mx-auto leading-tight">
            👉 Chrome bloquea el cierre por seguridad.<br />
            ¡Desliza esta pestaña hacia arriba para cerrarla!
          </p>
        )}
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center p-6 min-h-full overflow-y-auto text-center relative bg-black"
    >
      <AnimatePresence>
        {scanQRMode && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-5">
            <h3 className="text-white font-black mb-4 flex items-center gap-2"><QrCode className="w-6 h-6"/> Módulo de Escaneo Taller</h3>
            <div id="reader" className="w-full max-w-sm bg-white rounded-2xl overflow-hidden shadow-cyan-glow"></div>
            <button onClick={() => setScanQRMode(false)} className="mt-8 text-primary-cyan underline font-bold uppercase tracking-widest text-[10px]">
              Cancelar y Volver
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Botón Instalar App PWA */}
      {!isInstalled && !askingPin && !creatingPinForOldUser && (
        <button 
          onClick={handleInstallClick}
          className="relative group w-auto mx-auto mb-6 bg-black border border-primary-cyan/60 text-primary-cyan rounded-2xl py-3 px-8 text-[13px] font-black uppercase tracking-widest shadow-[0_0_15px_rgba(0,168,255,0.25)] hover:shadow-[0_0_35px_rgba(0,168,255,0.9)] hover:border-primary-cyan hover:bg-primary-cyan/10 transition-all duration-300 flex items-center justify-center gap-2 z-10"
        >
            <DownloadCloud className="w-5 h-5 group-hover:animate-bounce mr-1" />
            <span>Instalar Gravity Bike App</span>
        </button>
      )}

      <div className="mb-2 flex flex-col items-center justify-center">
        <img src="logo.png" alt="GRAVITY BIKE APP" className="h-28 md:h-36 object-contain" />
      </div>

      { !askingPin && !creatingPinForOldUser && (
        <div className="mb-8 text-center flex flex-col gap-1">
          { (commercialFlow !== 'login' || loginMode === 'business') ? (
              <>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">ACCESO SEGURO PARA ALIADOS</span>
                <h2 className="text-xl font-black text-amber-400 leading-none">Modo Taller y/o Tienda</h2>
              </>
            ) : (
              <>
                <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Acceso Seguro</h2>
              </>
            )
          }
        </div>
      )}

      <AnimatePresence mode="wait">
        {askingPin ? (
          <motion.div 
            key="pin-view"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`w-full max-w-sm p-8 rounded-[40px] border ${pinError ? 'border-danger-red bg-danger-red/5 animate-shake' : 'border-primary-cyan/30 bg-bg-card/50'} backdrop-blur-xl shadow-cyan-glow relative`}
          >
            <button 
              onClick={() => { setAskingPin(false); setPin(''); }}
              className="absolute top-6 right-6 text-slate-500 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 bg-primary-cyan/20 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-primary-cyan" />
              </div>
              <h3 className="text-white font-black text-lg uppercase tracking-widest">Ingresa tu PIN</h3>
              <p className="text-slate-400 text-[10px] font-bold mt-1 uppercase">Bóveda de Seguridad Activa</p>
              <p className="text-text-muted text-xs mt-3 leading-relaxed max-w-[250px]">Digita tu clave de 4 números para desbloquear tu perfil y acceder a tus beneficios.</p>
              {pinError && <p className="text-danger-red text-xs mt-2 animate-pulse font-black uppercase tracking-widest">{pinError}</p>}
            </div>

            <PinKeypad onComplete={handleVerifyPin} />
          </motion.div>
        ) : creatingPinForOldUser ? (
          <motion.div 
            key="create-pin-view"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`w-full max-w-sm p-8 rounded-[40px] border ${pinError ? 'border-danger-red bg-danger-red/5 animate-shake' : 'border-primary-cyan/30 bg-bg-card/50'} backdrop-blur-xl shadow-cyan-glow relative`}
          >
            <button 
              onClick={() => { setCreatingPinForOldUser(false); setPin(''); setFirstPin(''); }}
              className="absolute top-6 right-6 text-slate-500 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 bg-primary-cyan/20 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-primary-cyan" />
              </div>
              <h3 className="text-white font-black text-lg uppercase tracking-widest text-center leading-tight">
                {firstPin ? 'CONFIRMA TU PIN' : '¡Hola de nuevo!'}
              </h3>
              <p className="text-text-muted text-xs mt-3 leading-relaxed max-w-[250px]">
                {firstPin ? 'Vuelve a digitar los 4 números para confirmar tu Bóveda.' : 'Hemos mejorado la seguridad. Crea tu nuevo PIN de 4 dígitos para acceder a tu Bóveda.'}
              </p>
              {pinError && <p className="text-danger-red text-xs mt-2 animate-pulse font-black uppercase tracking-widest text-center">{pinError}</p>}
            </div>

            <PinKeypad onComplete={handleCreatePinForOldUser} />
          </motion.div>
        ) : (
          commercialFlow === 'login' && !registerSuccess && (
            <motion.div 
              key="phone-view"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="w-full max-w-sm space-y-4 mb-4"
            >
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input 
                  type="tel" 
                  value={celular}
                  onChange={e => setCelular(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Tu Celular" 
                  className="w-full pl-12 pr-4 py-3 rounded-2xl border border-slate-700 bg-bg-card/50 text-white font-black tracking-[0.15em] outline-none focus:border-primary-cyan focus:bg-primary-cyan/5 text-lg text-center shadow-inner transition-all" 
                />
              </div>
              
              <button 
                onClick={handleAuth}
                disabled={loading || celular.length < 10}
                className={`w-full h-12 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:shadow-none ${loginMode === 'business' ? 'bg-slate-800 border border-white/20' : 'bg-gradient-to-r from-primary-cyan to-blue-600 shadow-[0_0_15px_rgba(0,168,255,0.3)]'}`}
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
                {loading ? "Verificando..." : (loginMode === 'business' ? "Entrar" : "Entrar / Registrarme")}
              </button>

              {biometricAvailable && !loginMode && (
                <button 
                  onClick={handleBiometricAuth}
                  className="w-full flex items-center justify-center gap-3 py-4 bg-white/5 border border-white/10 rounded-2xl text-white/60 hover:text-white hover:bg-white/10 transition-all active:scale-95"
                >
                  <Fingerprint className="w-6 h-6 text-primary-cyan" />
                  <span className="text-[10px] font-black uppercase tracking-widest">Acceso Rápido con Huella</span>
                </button>
              )}

              {loginMode === 'business' && (
                <button 
                  onClick={() => {
                    Storage.remove('gravity_login_mode');
                    setLocalLoginMode(null);
                    setCommercialFlow('login');
                  }}
                  className="w-full text-center text-slate-500 text-[10px] font-bold py-2 uppercase tracking-widest hover:text-white transition-colors"
                >
                  Volver al Inicio (Modo Motero)
                </button>
              )}
            </motion.div>
          )
        )}
      </AnimatePresence>

      {!askingPin && !creatingPinForOldUser && commercialFlow === 'options' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm space-y-3 mb-6">
          <button 
            onClick={() => {
              setCommercialFlow('register');
              setRegStep(1);
            }}
            className="w-full py-4 bg-slate-800 border border-white/10 text-white font-bold rounded-xl text-xs uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Store className="w-4 h-4 text-amber-400" /> Registrar Taller y/o Tienda
          </button>
          
          <button 
            onClick={() => { 
                setLocalLoginMode('business');
                Storage.set('gravity_login_mode', 'business');
                setCommercialFlow('login'); 
                setTimeout(() => { document.querySelector('input')?.focus(); }, 100); 
            }}
            className="w-full py-4 bg-slate-800 border border-white/10 text-white font-bold rounded-xl text-xs uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <UserCheck className="w-4 h-4 text-primary-cyan" /> Ingreso Modo Propietario
          </button>

          <button 
            onClick={() => setScanQRMode(true)}
            className="w-full py-4 bg-slate-800 border border-white/10 text-white font-bold rounded-xl text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-md"
          >
            <QrCode className="w-4 h-4 text-green-400" /> Soy Empleado (Leer QR)
          </button>

          <button onClick={() => { Storage.remove('gravity_login_mode'); setLocalLoginMode(null); setCommercialFlow('login'); }} className="w-full text-center text-slate-500 text-[10px] font-bold py-2 uppercase">
            Volver
          </button>
        </motion.div>
      )}

      {commercialFlow === 'register' && !registerSuccess && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="w-full max-w-[420px] bg-slate-800/60 backdrop-blur-2xl border border-white/10 rounded-[35px] p-6 md:p-8 text-left shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] relative overflow-hidden"
        >
           {/* Barra de Progreso Superior */}
           <div className="absolute top-0 left-0 w-full h-1 bg-white/5">
             <motion.div 
                initial={{ width: '0%' }}
                animate={{ width: regStep === 1 ? '50%' : '100%' }}
                className="h-full bg-gradient-to-r from-primary-cyan to-amber-400"
             />
           </div>

           <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-black text-lg uppercase tracking-wider">
                {regStep === 1 ? 'Paso 1: Propietario' : 'Paso 2: Negocio'}
              </h3>
              <span className="text-sm text-slate-500 font-black">{regStep} / 2</span>
           </div>

           <AnimatePresence mode="wait">
             {regStep === 1 ? (
                <motion.div key="step1" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-xs text-slate-400 uppercase font-black ml-1 tracking-wider">Nombre del Propietario</label>
                      <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-primary-cyan transition-colors" />
                        <input 
                            type="text" 
                            placeholder="Como aparece en CC"
                            value={nombrePropietario}
                            onChange={e => setNombrePropietario(e.target.value.toUpperCase())}
                            className="w-full bg-black/40 border border-white/5 rounded-2xl py-3.5 pl-12 pr-4 text-white text-base font-bold outline-none focus:border-primary-cyan focus:bg-black/60 transition-all placeholder:text-slate-600 shadow-inner"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-400 uppercase font-black ml-1 tracking-wider">Cédula del Propietario</label>
                      <div className="relative group">
                        <IdCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-primary-cyan transition-colors" />
                        <input 
                            type="tel" 
                            placeholder="Número de Identidad"
                            value={cedulaPropietario}
                            onChange={e => setCedulaPropietario(e.target.value.replace(/\D/g, ''))}
                            className="w-full bg-black/40 border border-white/5 rounded-2xl py-3.5 pl-12 pr-4 text-white text-base font-bold outline-none focus:border-primary-cyan focus:bg-black/60 transition-all placeholder:text-slate-600 shadow-inner"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-slate-400 uppercase font-black ml-1 tracking-wider">Celular de Contacto</label>
                      <div className="relative group">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-primary-cyan transition-colors" />
                        <input 
                            type="tel" 
                            placeholder="Tu número principal"
                            value={celularTaller}
                            onChange={e => setCelularTaller(e.target.value.replace(/\D/g, '').slice(0,10))}
                            className="w-full bg-black/40 border border-white/5 rounded-2xl py-3.5 pl-12 pr-4 text-white text-base font-bold outline-none focus:border-primary-cyan focus:bg-black/60 transition-all placeholder:text-slate-600 shadow-inner"
                        />
                      </div>
                    </div>
                    
                    <button 
                      onClick={async () => {
                        if (!nombrePropietario || !cedulaPropietario || celularTaller.length < 10) {
                          alert("Completa todos los datos personales.");
                          return;
                        }
                        // Validación rápida de NIT duplicado (Más fiable que el celular)
                        const { data } = await supabase.from('solicitudes_taller').select('id').eq('nit', nitTaller).eq('estado', 'pendiente').limit(1);
                        if (data && data.length > 0) {
                          alert("🛑 Este taller (NIT) ya tiene una solicitud en trámite.");
                          return;
                        }
                        setRegStep(2);
                      }}
                      disabled={!nombrePropietario || !cedulaPropietario || celularTaller.length < 10}
                      className="w-full py-5 bg-gradient-to-r from-blue-600 to-primary-cyan text-white font-black rounded-2xl text-sm uppercase tracking-widest mt-6 flex items-center justify-center gap-2 disabled:opacity-40 border border-transparent disabled:border-white/5 transition-all shadow-lg shadow-primary-cyan/20 active:scale-95"
                    >
                      Siguiente Paso <ArrowRight className="w-5 h-5" />
                    </button>
                    <button onClick={() => setCommercialFlow('options')} className="w-full text-center text-slate-500 text-[11px] font-black py-2 uppercase tracking-widest hover:text-white transition-colors">
                      Cancelar Registro
                    </button>
                </motion.div>
             ) : (
                <motion.div key="step2" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="space-y-3">
                   <div className="space-y-2">
                      <label className="text-xs text-slate-400 uppercase font-black ml-1 tracking-wider">Nombre del Negocio</label>
                      <div className="relative group">
                        <Store className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-amber-400 transition-colors" />
                        <input 
                          type="text" 
                          placeholder="Ej: Taller Rayo McQueen"
                          value={nombreTaller}
                          onChange={e => setNombreTaller(e.target.value.toUpperCase())}
                          className="w-full bg-black/40 border border-white/5 rounded-2xl py-2.5 pl-12 pr-4 text-white text-base font-bold outline-none focus:border-amber-400 focus:bg-black/60 transition-all placeholder:text-slate-600 shadow-inner"
                        />
                      </div>
                   </div>
                   <div className="space-y-2">
                      <label className="text-xs text-slate-400 uppercase font-black ml-1 tracking-wider">Nit del Negocio</label>
                      <div className="relative group">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-amber-400 transition-colors" />
                        <input 
                          type="tel" 
                          placeholder="Número de NIT"
                          value={nitTaller}
                          onChange={e => setNitTaller(e.target.value.replace(/\D/g, ''))}
                          className="w-full bg-black/40 border border-white/5 rounded-2xl py-2.5 pl-12 pr-4 text-white text-base font-bold outline-none focus:border-amber-400 focus:bg-black/60 transition-all placeholder:text-slate-600 shadow-inner"
                        />
                      </div>
                   </div>

                   <label className="text-xs text-slate-400 uppercase font-black ml-1 tracking-wider block mt-4">Documentación Requerida</label>
                    <div className="grid grid-cols-2 gap-2">
                       {[
                         { k: 'cedula', t: 'FOTO C.C.', i: <UserCheck className="w-4 h-4"/> },
                         { k: 'camara', t: 'CÁMARA COM.', i: <FileText className="w-4 h-4"/> },
                         { k: 'rut', t: 'RUT', i: <FileText className="w-4 h-4"/> },
                         { k: 'recibo', t: 'RECIBO PUBLICO', i: <Camera className="w-4 h-4"/> }
                       ].map(doc => (
                         <label key={doc.k} className={`py-2 px-3 border rounded-xl flex items-center gap-3 cursor-pointer transition-all ${archivos[doc.k] ? 'bg-amber-500/10 border-amber-500/50 text-amber-400' : 'bg-black/30 border-white/5 text-slate-500 hover:border-white/20 hover:bg-black/50'}`}>
                           <div className={`p-1.5 rounded-lg ${archivos[doc.k] ? 'bg-amber-500/20' : 'bg-white/5'}`}>
                             {doc.i}
                           </div>
                           <span className="text-[9px] font-black uppercase tracking-wider">
                             {doc.t} {archivos[doc.k] && <span className="text-amber-500">✓</span>}
                           </span>
                           <input type="file" className="hidden" accept="image/*,application/pdf" onChange={e => handleFileChange(e, doc.k)} />
                         </label>
                       ))}
                    </div>

                   <button 
                     onClick={handleRegisterTaller}
                     disabled={subiendoTaller || !nombreTaller || !nitTaller || !archivos.cedula || !archivos.camara || !archivos.rut || !archivos.recibo}
                     className="w-full py-5 bg-gradient-to-r from-amber-600 to-amber-400 text-black font-black rounded-2xl text-sm uppercase tracking-widest shadow-xl shadow-amber-500/10 flex items-center justify-center gap-3 mt-6 disabled:opacity-40 border border-transparent disabled:border-white/5 transition-all active:scale-95"
                   >
                     {subiendoTaller ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                     {subiendoTaller ? 'Subiendo Expediente...' : 'Finalizar Registro'}
                   </button>
                   <button onClick={() => setRegStep(1)} className="w-full text-center text-slate-500 text-[11px] font-black py-2 uppercase tracking-widest hover:text-white transition-colors">
                      Volver al Paso 1
                   </button>
                </motion.div>
             )}
           </AnimatePresence>
        </motion.div>
      )}

      {registerSuccess && (
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm bg-bg-card border border-green-500/30 p-8 rounded-[40px] text-center shadow-2xl">
           <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
           <h3 className="text-white font-black text-xl mb-2">¡Solicitud Enviada!</h3>
           <p className="text-text-muted text-xs leading-relaxed mb-8">Nuestro equipo de **Auditoría de Campo** verificará tus documentos. Recibirás un mensaje al celular **{celularTaller}** cuando tu panel sea habilitado.</p>
           <button onClick={() => { setRegisterSuccess(false); setCommercialFlow('login'); }} className="w-full py-4 bg-white/5 border border-white/10 text-white font-bold rounded-xl space-x-2 text-xs uppercase tracking-widest">
              Entendido
           </button>
        </motion.div>
      )}

      {/* Ocultado temporalmente por solicitud del usuario (Módulo Tiendas/Talleres inactivo)
      {!askingPin && !creatingPinForOldUser && commercialFlow === 'login' && !registerSuccess && loginMode !== 'business' && (
        <div className="w-full max-w-sm mt-8 border-t border-white/5 pt-6">
          <button 
            onClick={() => setCommercialFlow('options')}
            className="w-full flex items-center justify-center gap-2 py-4 px-4 text-xs font-black uppercase tracking-[0.15em] text-slate-400 border border-white/10 rounded-2xl bg-white/5 hover:bg-white/10 transition-all shadow-inner"
          >
            <Store className="w-5 h-5" /> Soy Taller y/o Tienda Motera
          </button>
        </div>
      )}
      */}

      {/* Modal de Alertas Premium */}
      <AnimatePresence>
        {customAlert && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-sm bg-[#0B0F19] border border-white/10 rounded-[30px] p-8 text-center shadow-2xl relative overflow-hidden"
            >
              {/* Elementos Decorativos */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary-cyan to-transparent opacity-50"></div>
              
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/20">
                <ShieldAlert className="w-8 h-8 text-red-400" />
              </div>
              
              <h3 className="text-white font-black text-xl mb-3 tracking-wide">{customAlert.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-8">
                {customAlert.message}
              </p>
              
              <button 
                onClick={() => setCustomAlert(null)}
                className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-black rounded-xl text-xs uppercase tracking-[0.2em] transition-all active:scale-95"
              >
                Entendido
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Link de Privacidad Sutil para Play Store Compliance */}
      <div className="mt-8 mb-4 opacity-40">
        <p className="text-[10px] text-slate-500 font-medium tracking-tight">
          Al continuar, aceptas nuestras <a href="https://pikishub.com/gravitybike/privacidad.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors">Políticas de Privacidad</a>
        </p>
      </div>
    </motion.div>
  );
}
